import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Spinner, Table } from 'react-bootstrap';
import { Client } from '../../models/table-models';
import { CompanyLookupResult, getCompanyLookup } from '../../utils/clients-hooks';
import { ClientWarning } from '../../utils/Enums/ClientWarnings';
import { useTranslation } from 'react-i18next';

type Difference = { label: string; current: string; next: string };

/** Състояния от ТР, при които проверката преди фактура винаги показва диалога, дори без разлики в данните. */
const REGISTRY_STATUS_WARNINGS = new Set<string>([
    ClientWarning.CompanyDeleted,
    ClientWarning.CompanyInLiquidation,
    ClientWarning.CompanyInsolvent,
]);

const hasRegistryStatusWarning = (result: CompanyLookupResult) =>
    !!result.warnings?.some((warning) => REGISTRY_STATUS_WARNINGS.has(warning));

/**
 * Разлики между клиента и регистрите. МОЛ не се сравнява: не е задължителен реквизит на фактурата по ЗДДС
 * и вече не се печата, затова представляващият от ТР не се предлага за обновяване.
 */
export function compareRegistryClient(client: Client, result: CompanyLookupResult) {
    const changes: Partial<Client> = {};
    const differences: Difference[] = [];
    const text = (value: unknown) => String(value ?? '').trim();
    const addText = (key: 'companyName' | 'city' | 'companyAddress', label: string, value?: string | null) => {
        const comparable = (value: unknown) => {
            const normalized = text(value).toLocaleUpperCase('bg');
            return key === 'companyName' ? normalized.replace(/["'„“”‘’«»‹›]/g, '').trim() : normalized;
        };
        if (!text(value) || comparable(client[key]) === comparable(value)) return;
        changes[key] = text(value);
        differences.push({ label, current: text(client[key]), next: text(value) });
    };

    if (result.found) {
        addText('companyName', 'companyName', result.companyName);
        addText('city', 'city', result.city);
        addText('companyAddress', 'companyAddress', result.address);
    }

    if (result.vatStatus === 'VALID' || result.vatStatus === 'INVALID') {
        const registered = result.vatStatus === 'VALID';
        const vat = registered ? text(result.vat).replace(/^BG/i, '') : '';

        if (client.ddc !== registered) {
            differences.push({ label: 'ddc', current: client.ddc ? 'yes' : 'no', next: registered ? 'yes' : 'no' });
        }

        const currentVat = text(client.vat) ? `${client.vatPrefix || ''}${text(client.vat)}` : '';
        const nextVat = vat ? `BG${vat}` : '';

        if (currentVat !== nextVat) differences.push({ label: 'vat', current: currentVat, next: nextVat });

        if (client.ddc !== registered || currentVat !== nextVat) {
            changes.ddc = registered;
            changes.vat = vat;
            changes.vatPrefix = registered ? 'BG' : '';
        }
    }

    return { changes, differences };
}

export const ClientRegistryCheck = ({
    client,
    onConfirm,
    onContinue,
    onCancel,
    canUpdate = true,
    onVisibilityChange,
}: {
    client: Client;
    onConfirm: (client: Client) => Promise<void>;
    onContinue?: () => Promise<void>;
    onCancel?: () => void;
    canUpdate?: boolean;
    onVisibilityChange?: (visible: boolean) => void;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'registryCheck' });
    const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
    const [show, setShow] = useState(false);
    useEffect(() => { onVisibilityChange?.(show); }, [show, onVisibilityChange]);
    const mounted = useRef(true);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<CompanyLookupResult | null>(null);
    const [error, setError] = useState('');
    // Сравнява се с клиента от момента на проверката: след запис родителят подава вече обновения клиент
    // и таблицата с разлики не трябва да се сменя с „Няма разлики“, докато модалът се затваря.
    const [checkedClient, setCheckedClient] = useState(client);
    const comparison = result ? compareRegistryClient(checkedClient, result) : null;
    const changes = comparison?.changes ?? {};
    const hasChanges = Object.keys(changes).length > 0;

    const check = async () => {
        if (!onContinue) setShow(true);
        setBusy(true);
        setResult(null);
        setError('');
        try {
            const data = await getCompanyLookup(client.eik || '', true);

            if (!mounted.current) return;

            if (!data) throw new Error('Lookup failed');

            setCheckedClient(client);
            setResult(data);

            if (
                onContinue &&
                data.found &&
                ['VALID', 'INVALID'].includes(data.vatStatus || '') &&
                !hasRegistryStatusWarning(data) &&
                compareRegistryClient(client, data).differences.length === 0
            ) {
                await onContinue();
                if (mounted.current) setShow(false);
            } else {
                setShow(true);
            }
        } catch {
            if (mounted.current) {
                setError('checkFailed');
                setShow(true);
            }
        } finally {
            if (mounted.current) setBusy(false);
        }
    };
    useEffect(() => {
        mounted.current = true;
        if (onContinue) void check();
        return () => {
            mounted.current = false;
        };
        // Invoice mode is mounted once for each confirmation attempt.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const close = () => {
        setShow(false);
        onCancel?.();
    };

    /** Стойност в таблицата с разлики: ДДС регистрацията е да/не за превод, празното е тире. */
    const displayValue = (label: string, value: string) => (label === 'ddc' ? t(value) : value || '—');

    let confirmLabel = 'confirmAndUpdate';
    if (onContinue) confirmLabel = hasChanges ? 'updateAndContinue' : 'continueWithoutUpdate';

    // На заличена фирма не се издава фактура: в режим „фактура" остава само отказът.
    const invoiceBlocked = !!onContinue && !!result?.warnings?.includes(ClientWarning.CompanyDeleted);

    const keepCurrent = async () => {
        if (!onContinue || busy || invoiceBlocked) return;

        setBusy(true);
        setError('');

        try {
            await onContinue();
            if (mounted.current) setShow(false);
        } catch {
            if (mounted.current) setError('continueFailed');
        } finally {
            if (mounted.current) setBusy(false);
        }
    };

    const confirm = async () => {
        if (!comparison || busy || invoiceBlocked) return;

        setBusy(true);
        setError('');

        try {
            if (hasChanges) await onConfirm({ ...client, ...changes });
            else if (onContinue) await onContinue();
            if (mounted.current) setShow(false);
        } catch {
            if (mounted.current) setError('updateFailed');
        } finally {
            if (mounted.current) setBusy(false);
        }
    };

    return (
        <>
            {!onContinue && (
                <Button
                    type='button'
                    variant='outline-primary'
                    className='px-2 py-1'
                    disabled={busy || !client.eik?.trim()}
                    onClick={check}
                >
                    {t('check')}
                </Button>
            )}
            <Modal show={show} onHide={() => !busy && close()} size='lg' backdrop='static'>
                <Modal.Header closeButton={!busy}>
                    <Modal.Title>{t('title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        {client.companyName} — {t('eik')} {client.eik}
                    </p>
                    <p>{t('description')}</p>
                    {busy && (
                        <output className='d-block'>
                            <Spinner animation='border' size='sm' /> {t('processing')}
                        </output>
                    )}
                    {error && <Alert variant='danger'>{t(error)}</Alert>}
                    {result && (
                        <>
                            {(result.vatStatus === 'VALID' || result.vatStatus === 'INVALID') && (
                                <p>
                                    VIES: {t(result.vatStatus === 'VALID' ? 'vatValid' : 'vatInvalid')} ({result.vat}).
                                </p>
                            )}
                            {result.warnings?.includes(ClientWarning.CompanyDeleted) && (
                                // Проверката е за вече съществуващ клиент - съобщението е само, че фирмата е заличена.
                                <Alert variant='danger'>
                                    {invoiceBlocked ? t('invoiceBlockedDeleted') : tCommon('companyDeletedInRegister')}
                                </Alert>
                            )}
                            {[ClientWarning.CompanyInLiquidation, ClientWarning.CompanyInsolvent]
                                .filter((warning) => result.warnings?.includes(warning))
                                .map((warning) => (
                                    <Alert key={warning} variant='warning'>{tCommon(warning)}</Alert>
                                ))}
                            {!result.found && <Alert variant='warning'>{t('companyUnavailable')}</Alert>}
                            {result.vatStatus !== 'VALID' && result.vatStatus !== 'INVALID' && (
                                <Alert variant='warning'>{t('vatUnavailable')}</Alert>
                            )}
                            {comparison && comparison.differences.length > 0 ? (
                                <>
                                    <Table bordered responsive>
                                        <thead>
                                            <tr>
                                                <th>{t('field')}</th>
                                                <th>{t('currentValue')}</th>
                                                <th>{t('registryValue')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {comparison.differences.map((row) => (
                                                <tr key={row.label}>
                                                    <td>{t(`fields.${row.label}`)}</td>
                                                    <td>{displayValue(row.label, row.current)}</td>
                                                    <td>{displayValue(row.label, row.next)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                    <p>{t('confirmChanges')}</p>
                                </>
                            ) : (
                                <Alert variant='info'>
                                    {!result.found && result.vatStatus !== 'VALID' && result.vatStatus !== 'INVALID'
                                        ? t('noVerifiedData')
                                        : t('noDifferences')}
                                </Alert>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button type='button' variant='secondary' disabled={busy} onClick={close}>
                        {t(onContinue ? 'cancel' : 'close')}
                    </Button>
                    {onContinue && (
                        <>
                            <Button type='button' variant='outline-primary' disabled={busy} onClick={check}>
                                {t('retry')}
                            </Button>
                            <Button type='button' variant='outline-primary' disabled={busy || invoiceBlocked} onClick={keepCurrent}>
                                {t('keepAndContinue')}
                            </Button>
                        </>
                    )}
                    {!!comparison?.differences.length && canUpdate && (hasChanges || onContinue) && (
                        <Button type='button' disabled={busy || invoiceBlocked} onClick={confirm}>
                            {t(confirmLabel)}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
};
